import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import './Login.css';

// Supabase 인증 에러 메시지를 번역 키로 매핑
function authErrorKey(message) {
  const m = (message || '').toLowerCase();
  if (m.includes('invalid login credentials')) return 'login.errInvalidCredentials';
  if (m.includes('email not confirmed')) return 'login.errEmailNotConfirmed';
  if (m.includes('user already registered') || m.includes('already been registered'))
    return 'login.errAlreadyRegistered';
  if (m.includes('password should be at least')) return 'login.errPasswordShort';
  if (m.includes('unable to validate email') || m.includes('invalid email'))
    return 'login.errInvalidEmail';
  if (m.includes('rate limit') || m.includes('too many')) return 'login.errRateLimit';
  return 'login.errGeneric';
}

function Login({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [infoMsg, setInfoMsg] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // 팝업 닫힐 때 입력/상태 초기화
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setPassword('');
      setErrorMsg('');
      setInfoMsg('');
      setSubmitting(false);
      setMode('signin');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleGoogleLogin = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
          queryParams: {
            prompt: 'select_account',
            access_type: 'offline',
          },
        },
      });
      if (error) throw error;
    } catch (error) {
      console.error('Google 로그인 에러:', error.message);
      alert(t('login.googleError'));
    }
  };

  // 이메일/비밀번호 회원가입·로그인 (PG사 심사용 — 추후 제거 예정)
  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setInfoMsg('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password) {
      setErrorMsg(t('login.emailPasswordRequired'));
      return;
    }
    if (mode === 'signup' && password.length < 6) {
      setErrorMsg(t('login.errPasswordShort'));
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'signup') {
        const { data, error } = await supabase.auth.signUp({
          email: trimmedEmail,
          password,
          options: {
            emailRedirectTo: window.location.origin,
            data: { full_name: trimmedEmail.split('@')[0] },
          },
        });
        if (error) throw error;
        if (data.session) {
          // 이메일 인증 비활성화 → 바로 로그인됨
          onClose();
        } else {
          // 이메일 인증 활성화 → 확인 메일 안내
          setInfoMsg(t('login.signupConfirmSent'));
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: trimmedEmail,
          password,
        });
        if (error) throw error;
        onClose();
      }
    } catch (error) {
      console.error('이메일 인증 에러:', error.message);
      setErrorMsg(t(authErrorKey(error.message)));
    } finally {
      setSubmitting(false);
    }
  };

  // 비밀번호 재설정 메일 발송 (PG사 심사용 — 추후 제거 예정)
  const handleResetRequest = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setInfoMsg('');

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrorMsg(t('login.emailRequired'));
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setInfoMsg(t('login.resetSent'));
    } catch (error) {
      console.error('비밀번호 재설정 메일 에러:', error.message);
      setErrorMsg(t(authErrorKey(error.message)));
    } finally {
      setSubmitting(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setErrorMsg('');
    setInfoMsg('');
  };

  const toggleMode = () => {
    switchMode(mode === 'signin' ? 'signup' : 'signin');
  };

  return (
    <div className="login-overlay" onClick={onClose}>
      <div className="login-modal" onClick={(e) => e.stopPropagation()}>
        <button className="login-close-btn" onClick={onClose}>
          ×
        </button>
        <div className="login-content">
          <h1 className="login-title">
            Lansly<span className="logo-dot" aria-hidden="true" />
          </h1>

          <p className="login-subtitle">{t('login.subtitle')}</p>
          <button className="google-login-btn" onClick={handleGoogleLogin}>
            <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {t('login.google')}
          </button>

          <div className="login-divider">
            <span>{t('login.or')}</span>
          </div>

          <form
            className="login-email-form"
            onSubmit={mode === 'reset' ? handleResetRequest : handleEmailAuth}
          >
            <input
              type="email"
              className="login-input"
              placeholder={t('login.email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              disabled={submitting}
            />
            {mode !== 'reset' && (
              <input
                type="password"
                className="login-input"
                placeholder={mode === 'signup' ? t('login.passwordSignup') : t('login.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                disabled={submitting}
              />
            )}

            {errorMsg && <p className="login-msg login-msg--error">{errorMsg}</p>}
            {infoMsg && <p className="login-msg login-msg--info">{infoMsg}</p>}

            <button type="submit" className="login-email-btn" disabled={submitting}>
              {submitting
                ? t('login.processing')
                : mode === 'reset'
                  ? t('login.sendResetMail')
                  : mode === 'signup'
                    ? t('login.signUp')
                    : t('login.signIn')}
            </button>

            {mode === 'signin' && (
              <button
                type="button"
                className="login-forgot-btn"
                onClick={() => switchMode('reset')}
              >
                {t('login.forgotPassword')}
              </button>
            )}
          </form>

          <p className="login-toggle">
            {mode === 'reset' ? (
              <button type="button" className="login-toggle-btn" onClick={() => switchMode('signin')}>
                {t('login.backToLogin')}
              </button>
            ) : (
              <>
                {mode === 'signup' ? t('login.haveAccount') : t('login.noAccount')}{' '}
                <button type="button" className="login-toggle-btn" onClick={toggleMode}>
                  {mode === 'signup' ? t('login.signIn') : t('login.signUp')}
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
